import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@ui/card.tsx';
import {
  PackageCheckIcon,
  HelpCircleIcon,
  HomeIcon,
  SquareArrowOutUpRight,
  ClipboardListIcon,
  AlertTriangleIcon
} from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button } from '@ui/button.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/table.tsx';
import { useStepper } from '@client/pages/cart/cart.tsx';
import { type ComponentProps, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import pointsTranslation from '@r/translations/points-translation.ts';
import { useBalance } from '@r/context/balance-context.ts';
import { FormattedHtml } from '@r/components/formatted-html.tsx';
import type { OrderOrderReadModel } from '@r/api/generated/client.schemas.ts';

type OrderResponse = OrderOrderReadModel;
type OrdersResponse = OrderResponse[];

const OrderResult = () => {
  const { t } = useTranslation();
  const stepper = useStepper();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrdersResponse>([]);
  const { balance, updateBalance } = useBalance();
  const supportEmail = 'support@fiddy.app';

  //TODO: Implement deliveryDelay
  const deliveryDelay = false;

  const calculateTotal = (orders: OrdersResponse): number => {
    return orders
      .filter((order) => order.status !== 'order.status.failed')
      .reduce((total, order) => total + Number(order.point?.amount ?? 0), 0);
  };

  useEffect(() => {
    const ordersResponse = stepper.getMetadata('step-4')?.ordersResponse;

    if (ordersResponse) {
      setOrders(ordersResponse);
      const total = calculateTotal(ordersResponse);
      updateBalance(balance + total);
      stepper.resetMetadata();
    } else {
      navigate({ to: '/cart' }).then((r) => {
        console.warn('No orders found, redirecting to cart', r);
      });
    }
  }, [navigate]);

  if (!orders || orders.length === 0) {
    return null;
  }

  const completedOrders = orders.filter((order) => order.status !== 'order.status.failed');
  const failedOrders = orders.filter((order) => order.status === 'order.status.failed');

  const OrdersTable = ({ orders, isError }: { orders: OrdersResponse; isError?: boolean }) => (
    <Table>
      <TableHeader className="bg-transparent">
        <TableRow
          className={`hover:bg-transparent border-dashed ${isError ? 'border-destructive' : 'border-muted-foreground/25'}`}
        >
          <TableHead className="align-top">{t('order.quantity')}</TableHead>

          <TableHead className="align-top">{t('order.item')}</TableHead>

          <TableHead className="align-top text-end">
            {t('order.product.point.unit.price')}

            <span className="hidden sm:inline">
              {t('currency.in', { currency: t('point.name_other') })}
            </span>

            <p className="text-xs sm:hidden text-muted-foreground">({t('point.name_other')})</p>
          </TableHead>

          <TableHead className="align-top text-end">
            {t('order.amount')}

            <span className="hidden sm:inline">
              {t('currency.in', { currency: t('point.name_other') })}
            </span>

            <p className="text-xs sm:hidden text-muted-foreground">({t('point.name_other')})</p>
          </TableHead>
        </TableRow>
      </TableHeader>

      {orders.map((order) => (
        <TableBody
          key={order.id}
          className={`border-b border-dashed last:border-none ${isError ? 'border-destructive' : 'border-muted-foreground/25'}`}
        >
          {order.orderProducts.map((item, i) => (
            <TableRow
              key={`${i}${item.product.id}`}
              className={`border-b border-dashed hover:bg-transparent ${isError ? 'border-destructive' : 'border-muted-foreground/25'}`}
            >
              <TableCell>{item.quantity}</TableCell>

              <TableCell>
                <FormattedHtml as="span" text={item.product.name} />
                &nbsp;
                {item.product.dynamicPrice &&
                  item.amountUnitPrice &&
                  t('item.dynamic.price', {
                    amount: item.amountUnitPrice,
                    currency: '€'
                  })}
              </TableCell>

              <TableCell>
                {!item.product.dynamicPrice && <p className="text-end">{item.pointUnitPrice}</p>}
              </TableCell>

              <TableCell>
                <p className="text-end">
                  {item.product.dynamicPrice
                    ? item.pointUnitPrice
                    : item.pointUnitPrice * item.quantity}
                </p>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      ))}
    </Table>
  );

  const MailtoLink = (props: ComponentProps<'a'>) => {
    return (
      <a {...props} className="text-primary hover:underline">
        {props.children}
      </a>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {completedOrders.length >= 1 && (
        <>
          <h2 className="text-[2rem] leading-[2.75rem] font-semibold">
            {t('order.confirmation.title')}
          </h2>

          <div className="p-6 space-y-5 rounded-3xl bg-dark text-light">
            <p className="flex gap-5 items-center text-3xl font-semibold">
              <PackageCheckIcon size={30} className="hidden sm:block" />
              {t('order.success.message')}
            </p>

            {deliveryDelay && <p>{t('order.delivery.delay.notice')}</p>}
          </div>

          <Card className="border-none">
            <CardHeader>
              <CardTitle className="flex gap-4">
                <ClipboardListIcon size={30} />
                <p className="text-2xl font-semibold">{t('order.reminder.delivery')}</p>
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-5">
              <div className="border-b border-muted-foreground/25" />
              <OrdersTable orders={completedOrders} />
              <div className="border-b border-muted-foreground/25" />
            </CardContent>

            <CardFooter className="flex-col items-start space-y-5">
              <div className="flex justify-between w-full">
                <p className="font-semibold">{t('order.total.amount')}</p>
                <p className="text-xl font-bold">
                  {pointsTranslation(t, Math.abs(calculateTotal(orders)), true)}
                </p>
              </div>

              <div className="space-y-5">
                <ul className="pl-5 list-disc">
                  <li>{t('order.delivery.email.notice')}</li>
                  <li>{t('order.delivery.notice')}</li>
                </ul>
              </div>
            </CardFooter>
          </Card>
        </>
      )}

      {failedOrders && failedOrders.length > 0 && (
        <Card className="border-2 border-destructive">
          <CardHeader>
            <CardTitle className="flex gap-4 text-destructive">
              <AlertTriangleIcon size={30} />
              <p className="text-2xl font-semibold">{t('order.error.response.message')}</p>
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-5">
            <div className="border-b border-muted-foreground/25" />
            <OrdersTable orders={failedOrders} />
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-12 gap-4">
        {completedOrders.length >= 1 && (
          <Card className="border-none col-span-12 md:col-span-6">
            <CardHeader>
              <CardTitle className="flex gap-4">
                <HelpCircleIcon size={30} />
                <p className="text-2xl font-semibold">{t('order.tracking.title')}</p>
              </CardTitle>
            </CardHeader>

            <CardContent>
              <p>
                {t('order.confirmation.email.sent')}
                <Link className="text-primary hover:underline" to={'/account'}>
                  {t('menu.account.informations')}
                </Link>
                .
              </p>
            </CardContent>
          </Card>
        )}

        <Card className="border-none col-span-12 md:col-span-6">
          <CardHeader>
            <CardTitle className="flex gap-4">
              <HelpCircleIcon size={30} />
              <p className="text-2xl font-semibold">{t('order.question.title')}</p>
            </CardTitle>
          </CardHeader>

          <CardContent>
            <p>
              <Trans
                i18nKey="order.question.message"
                values={{
                  email: supportEmail
                }}
                components={{
                  Link: <Link to="/contact" className="text-primary hover:underline" />,
                  Mailto: <MailtoLink href={`mailto:${supportEmail}`} />
                }}
              />
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-y-5 justify-center w-full sm:flex-nowrap xs:gap-x-20 sm:pt-10">
        <Link to={'/'} className="w-full sm:w-auto">
          <Button rounded="full" variant="outline" className="w-full sm:w-auto">
            <HomeIcon size={20} />
            {t('common.back.home')}
          </Button>
        </Link>

        <Link to={'/orders'} className="w-full sm:w-auto">
          <Button rounded="full" className="w-full sm:w-auto">
            <SquareArrowOutUpRight size={20} />
            {t('order.track.orders')}
          </Button>
        </Link>
      </div>
    </div>
  );
};

export { OrderResult };
